import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Mail,
  Phone,
  Calendar,
  Users,
  Building2,
  Briefcase,
  Eye,
  MoreHorizontal,
  X,
  CheckCircle2,
  XCircle,
  UserPlus,
  Loader2,
} from "lucide-react";
import SearchBar from "../../../components/ui/SearchBar";
import FilterDropdown from "../../../components/ui/FilterDropdown";
import StatusBadge from "../../../components/ui/StatusBadge";
import KpiCard from "../../../components/ui/KpiCard";
import DataTable from "../../../components/ui/DataTable";
import { adminService } from "../services/adminService";

const PAGE_SIZE = 10;

const normalizeStatus = (statusValue) => {
  switch (statusValue) {
    case "Pending":
      return "pending";
    case "Contacted":
      return "contacted";
    case "NotContacted":
      return "not_contacted";
    default:
      return (statusValue || "").toLowerCase();
  }
};

const getPhoneDigits = (phoneValue = "") => String(phoneValue).replace(/\D/g, "");
const hasValidPhone = (phoneValue = "") => getPhoneDigits(phoneValue).length >= 3;

const mapContact = (contact) => {
  const rawPhone = contact.phone || "";
  
  // Check if account was created by verifying backend fields
  const accountCreatedAt = contact.account_created_at;
  const linkedUserId = contact.user_id;
  
  // Account is created only if backend has account_created_at or user_id value
  const accountCreated = Boolean(accountCreatedAt || linkedUserId);

  return {
    id: contact.id,
    company: contact.company_name,
    person: contact.name,
    email: contact.email,
    phone: rawPhone || "—",
    phoneRaw: rawPhone,
    employees: contact.no_of_employees ?? "—",
    // Coming Soon plans feed this same table; without the plan every lead
    // reads as "Enterprise". Older rows predate the column and stay blank.
    interestPlan: contact.interest_plan || "Enterprise (legacy)",
    requestedOn: contact.created_at
      ? new Date(contact.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—",
    status: normalizeStatus(contact.status),
    accountCreated,
    accountCreatedAt,
    hasValidPhone: hasValidPhone(rawPhone),
  };
};

const STATUS_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Pending", value: "Pending" },
  { label: "Contacted", value: "Contacted" },
  { label: "Not Contacted", value: "NotContacted" },
];

const CompanyCell = ({ company }) => (
  <div className="flex items-center gap-2.5">
    <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
      <Building2 className="w-4 h-4 text-purple-500" />
    </div>
    <span className="text-sm font-medium text-brand-text whitespace-nowrap">{company}</span>
  </div>
);

const Toast = ({ type, message, onDismiss }) => {
  useEffect(() => {
    const timeoutId = setTimeout(onDismiss, 4500);
    return () => clearTimeout(timeoutId);
  }, [onDismiss]);

  const isSuccess = type === "success";

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] pointer-events-none">
      <div
        className={`pointer-events-auto flex items-center gap-2.5 px-5 py-3 rounded-2xl text-sm font-semibold text-white ${
          isSuccess ? "bg-emerald-600" : "bg-danger"
        }`}
      >
        {isSuccess ? (
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 flex-shrink-0" />
        )}
        {message}
      </div>
    </div>
  );
};

const ConfirmCreateAccountModal = ({ target, creating, onConfirm, onCancel }) => {
  if (!target) return null;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 border border-surface-border">
        <h3 className="text-lg font-semibold text-brand-text">Create enterprise account?</h3>
        <p className="text-sm text-brand-muted mt-2">
          Continue to create account for <span className="font-semibold text-brand-text">{target.company}</span>.
        </p>

        <div className="mt-4 space-y-2 text-sm text-brand-text">
          <p>
            <span className="text-brand-muted">Contact:</span> {target.person}
          </p>
          <p>
            <span className="text-brand-muted">Email:</span> {target.email}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={creating}
            className="h-10 px-4 rounded-xl border border-surface-border text-sm font-semibold text-brand-text disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={creating}
            className="h-10 px-4 rounded-xl bg-[#5B4FCF] text-white text-sm font-semibold hover:bg-[#4f43be] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            {creating ? "Creating..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AccountCreatedAction = () => (
  <>
    <style>
      {`@keyframes ac-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }`}
    </style>
    <div
      className="flex items-center justify-center gap-2 relative overflow-hidden border-l-[2.5px] border-green-600 h-full w-full px-3"
      style={{ background: "rgba(22,163,74,0.08)", minHeight: "48px" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
          backgroundSize: "200% 100%",
          animation: "ac-shimmer 2.5s infinite",
        }}
      />
      <div
        className="relative w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(22,163,74,0.18)" }}
      >
        <CheckCircle2 className="w-3 h-3 text-green-600" />
      </div>
      <span className="relative text-xs font-medium text-green-700 whitespace-nowrap">
        Account created
      </span>
    </div>
  </>
);

const ContactDetailModal = ({
  contact,
  onClose,
  onMarkContacted,
  onMarkNotContacted,
  onRequestCreateAccount,
}) => {
  if (!contact) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg border border-surface-border">
        <div className="px-6 pt-5 pb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-brand-text">Contact Request Details</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-brand-muted hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-5 flex flex-col gap-5">
          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-brand-text">{contact.company}</p>
                <p className="text-xs text-brand-muted">{contact.person}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={contact.status} />
              {contact.accountCreated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                  Created
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            {[
              { label: "Email Address", Icon: Mail, value: contact.email },
              { label: "Phone Number", Icon: Phone, value: contact.phone },
              { label: "No.of Employee", Icon: Users, value: contact.employees },
              { label: "Interested In", Icon: Briefcase, value: contact.interestPlan },
              { label: "Requested On", Icon: Calendar, value: contact.requestedOn },
            ].map(({ label, Icon, value }) => (
              <div key={label}>
                <p className="text-xs text-brand-muted mb-1.5">{label}</p>
                <div className="flex items-center gap-2 text-sm text-brand-text">
                  <Icon className="w-4 h-4 text-brand-muted flex-shrink-0" />
                  {value}
                </div>
              </div>
            ))}
          </div>

          {contact.accountCreated ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 font-medium">
              Account already created for this contact.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 pt-1">
              <button
                onClick={() => {
                  onMarkNotContacted(contact.id);
                  onClose();
                }}
                className="py-3 rounded-xl border border-orange-400 text-orange-500 text-sm font-semibold hover:bg-orange-50 transition-colors"
              >
                Mark as Not Contacted
              </button>
              <button
                disabled={!contact.hasValidPhone}
                onClick={() => {
                  onRequestCreateAccount(contact);
                  onClose();
                }}
                className="py-3 rounded-xl border border-purple-400 text-purple-500 text-sm font-semibold hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Account
              </button>
              <button
                onClick={() => {
                  onMarkContacted(contact.id);
                  onClose();
                }}
                className="py-3 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Mark as Contacted
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ActionsMenu = ({
  contact,
  onView,
  onMarkContacted,
  onMarkNotContacted,
  onRequestCreateAccount,
  isCreating,
}) => {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (btnRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  if (contact.accountCreated) {
    return <AccountCreatedAction />;
  }

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 176;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < menuHeight ? rect.top - menuHeight : rect.bottom + 4;
      setPos({ top, left: rect.right - 256 });
    }
    setOpen((prev) => !prev);
  };

  const createDisabled = !contact.hasValidPhone || isCreating;

  return (
    <>
      <div className="w-full h-full flex items-center justify-center px-4 py-3">
        <button
          ref={btnRef}
          onClick={toggle}
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-brand-muted hover:bg-surface hover:text-brand-text transition-colors"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
            className="w-64 bg-white border border-surface-border rounded-xl shadow-lg py-1"
          >
            <button
              onClick={() => {
                setOpen(false);
                onView(contact);
              }}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors"
            >
              <Eye className="w-4 h-4 text-blue-500" /> View
            </button>

            <button
              disabled={isCreating}
              onClick={() => {
                setOpen(false);
                onMarkContacted(contact.id);
              }}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Mark as Contacted
            </button>

            <button
              disabled={isCreating}
              onClick={() => {
                setOpen(false);
                onMarkNotContacted(contact.id);
              }}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="whitespace-nowrap">Mark as Not Contacted</span>
            </button>

            <button
              disabled={createDisabled}
              onClick={() => {
                if (createDisabled) return;
                setOpen(false);
                onRequestCreateAccount(contact);
              }}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 text-purple-500" />
              )}
              {isCreating
                ? "Creating..."
                : contact.hasValidPhone
                  ? "Create Account"
                  : "Create Account (Phone required)"}
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

const AdminEnterpriseContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewContact, setViewContact] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, notContacted: 0 });
  const [toast, setToast] = useState(null);
  const [createTarget, setCreateTarget] = useState(null);
  const [creatingAccountId, setCreatingAccountId] = useState(null);
  const [createdEnterpriseAccounts, setCreatedEnterpriseAccounts] = useState({});

  const fetchStats = useCallback(() => {
    adminService
      .getEnterpriseContacts({ limit: 1 })
      .then((res) => {
        const summary = res?.data?.summary ?? {};
        setStats({
          total: summary.total ?? 0,
          pending: summary.pending ?? 0,
          contacted: summary.contacted ?? 0,
          notContacted: summary.notContacted ?? 0,
        });
      })
      .catch(() => {});
  }, []);

  const fetchContacts = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { page, limit: PAGE_SIZE, sort_by: "created_at", sort_order: "desc" };
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== "all") params.status = statusFilter;

    adminService
      .getEnterpriseContacts(params)
      .then((res) => {
        if (cancelled) return;

        const list = res?.data?.inquiries ?? [];
        const mappedContacts = list.map((item) => {
          const mapped = mapContact(item);

          if (createdEnterpriseAccounts[mapped.id]) {
            return {
              ...mapped,
              accountCreated: true,
              accountCreatedAt: mapped.accountCreatedAt || new Date().toISOString(),
            };
          }

          return mapped;
        });

        setContacts(mappedContacts);

        const meta = res?.data?.pagination ?? res?.pagination ?? {};
        setTotalPages(meta.totalPages ?? (Math.ceil((meta.total ?? 0) / PAGE_SIZE) || 1));
        setTotalCount(meta.total ?? list.length);

        const summary = res?.data?.summary;
        if (summary) {
          setStats({
            total: summary.total ?? 0,
            pending: summary.pending ?? 0,
            contacted: summary.contacted ?? 0,
            notContacted: summary.notContacted ?? 0,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, search, statusFilter, createdEnterpriseAccounts]);

  useEffect(() => {
    const cancel = fetchContacts();
    return cancel;
  }, [fetchContacts]);

  const updateStatus = useCallback(
    async (id, action) => {
      const normalized = action === "contacted" ? "contacted" : "not_contacted";

      setContacts((prev) => prev.map((contact) => (contact.id === id ? { ...contact, status: normalized } : contact)));
      setViewContact((prev) => (prev?.id === id ? { ...prev, status: normalized } : prev));

      try {
        if (action === "contacted") {
          await adminService.markEnterpriseContactContacted(id);
        } else {
          await adminService.markEnterpriseContactNotContacted(id);
        }
        fetchStats();
      } catch {
        fetchContacts();
      }
    },
    [fetchStats, fetchContacts]
  );

  const markContacted = useCallback((id) => updateStatus(id, "contacted"), [updateStatus]);
  const markNotContacted = useCallback((id) => updateStatus(id, "not_contacted"), [updateStatus]);

  const requestCreateAccount = useCallback((contact) => {
    if (!contact || contact.accountCreated || createdEnterpriseAccounts[contact.id]) return;

    if (!contact.hasValidPhone) {
      setToast({ type: "error", message: "Phone number is required (minimum 3 digits)." });
      return;
    }

    setCreateTarget(contact);
  }, [createdEnterpriseAccounts]);

  const confirmCreateAccount = useCallback(async () => {
    if (!createTarget) return;

    setCreatingAccountId(createTarget.id);
    setError(null);

    try {
      const response = await adminService.createEnterpriseContactAccount(createTarget.id);
      const createdEmail = response?.data?.user?.email || createTarget.email;

      setCreatedEnterpriseAccounts((prev) => ({
        ...prev,
        [createTarget.id]: true,
      }));

      setContacts((prev) =>
        prev.map((contact) =>
          contact.id === createTarget.id
            ? {
                ...contact,
                status: "contacted",
                accountCreated: true,
                accountCreatedAt: new Date().toISOString(),
              }
            : contact
        )
      );

      setViewContact((prev) =>
        prev?.id === createTarget.id
          ? {
              ...prev,
              status: "contacted",
              accountCreated: true,
              accountCreatedAt: new Date().toISOString(),
            }
          : prev
      );

      setToast({ type: "success", message: `Account created! Credentials sent to ${createdEmail}` });
      setCreateTarget(null);
      fetchStats();
      fetchContacts();
    } catch (err) {
      setToast({ type: "error", message: err.message || "Failed to create account." });
    } finally {
      setCreatingAccountId(null);
    }
  }, [createTarget, fetchContacts, fetchStats]);

  const total = stats.total;
  const pending = stats.pending;
  const contacted = stats.contacted;
  const notContacted = stats.notContacted;

  const columns = [
    {
      header: "Company",
      render: (contact) => <CompanyCell company={contact.company} />,
    },
    {
      header: "Contact Person",
      render: (contact) => <span className="text-sm text-brand-text whitespace-nowrap">{contact.person}</span>,
    },
    {
      header: "Email & Phone",
      render: (contact) => (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs text-brand-muted">
            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
            {contact.email}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-brand-muted">
            <Phone className="w-3.5 h-3.5 flex-shrink-0" />
            {contact.phone}
          </span>
        </div>
      ),
    },
    {
      header: "Requested On",
      render: (contact) => (
        <div className="flex items-center gap-1.5 text-sm text-brand-muted whitespace-nowrap">
          <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
          {contact.requestedOn}
        </div>
      ),
    },
    {
      header: "Status",
      render: (contact) => <StatusBadge status={contact.status} />,
    },
    {
      header: "Actions",
      render: (contact) => {
        const effectiveContact = {
          ...contact,
          accountCreated: Boolean(contact.accountCreated || createdEnterpriseAccounts[contact.id]),
        };

        return (
        <ActionsMenu
          contact={effectiveContact}
          onView={setViewContact}
          onMarkContacted={markContacted}
          onMarkNotContacted={markNotContacted}
          onRequestCreateAccount={requestCreateAccount}
          isCreating={creatingAccountId === contact.id}
        />
        );
      },
      noPadding: true,
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-5">
        <div>
          <h1 className="text-2xl font-bold text-brand-text">Enterprise Contact Requests</h1>
          <p className="text-sm text-brand-muted mt-0.5">Manage enterprise users requesting custom access.</p>
        </div>

        <div className="flex gap-4 flex-wrap">
          <KpiCard label="Total Requests" value={total} color="text-blue-600" />
          <KpiCard label="Pending" value={pending} color="text-amber-500" />
          <KpiCard label="Contacted" value={contacted} color="text-green-500" />
          <KpiCard label="Not Contacted" value={notContacted} color="text-red-500" />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <SearchBar
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Search by company, person or email…"
            className="max-w-md"
          />
          <div className="flex-1" />
          <FilterDropdown
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
            placeholder="All Status"
          />
        </div>

        {error && (
          <div className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-xl px-4 py-3">{error}</div>
        )}

        <DataTable
          columns={columns}
          rows={contacts}
          loading={loading}
          emptyText="No contacts found."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
        />
      </div>

      {viewContact && (
        <ContactDetailModal
          contact={{
            ...viewContact,
            accountCreated: Boolean(viewContact.accountCreated || createdEnterpriseAccounts[viewContact.id]),
          }}
          onClose={() => setViewContact(null)}
          onMarkContacted={markContacted}
          onMarkNotContacted={markNotContacted}
          onRequestCreateAccount={requestCreateAccount}
        />
      )}

      <ConfirmCreateAccountModal
        target={createTarget}
        creating={Boolean(createTarget && creatingAccountId === createTarget.id)}
        onConfirm={confirmCreateAccount}
        onCancel={() => {
          if (!creatingAccountId) setCreateTarget(null);
        }}
      />

      {toast && <Toast type={toast.type} message={toast.message} onDismiss={() => setToast(null)} />}
    </>
  );
};

export default AdminEnterpriseContactsPage;
